<template>
  <AnalyticsCard
    title="By country"
    subtitle="Visitors, and how many of them booked"
    :loading="loading"
    :empty="!loading && data.length === 0"
    empty-title="No location data yet"
    skeleton="list"
    :rows="6"
    :height="320"
  >
    <div class="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-100 dark:border-gray-700">
      <span class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Country</span>
      <span class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider w-14 text-right">Visitors</span>
      <span class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider w-12 text-right">Booked</span>
    </div>

    <div class="max-h-[268px] overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800/60">
      <div v-for="country in data" :key="country.code">
        <button type="button" @click="toggle(country.code)"
          class="w-full grid grid-cols-[1fr_auto_auto] items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors text-left">
          <span class="flex items-center gap-2 min-w-0">
            <span class="text-base leading-none flex-shrink-0">{{ flag(country.code) }}</span>
            <span class="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{{ country.name }}</span>
            <svg class="w-3 h-3 text-gray-300 dark:text-gray-600 transition-transform duration-150 flex-shrink-0"
              :class="expanded.has(country.code) ? 'rotate-90' : ''"
              fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m9 18 6-6-6-6"/>
            </svg>
          </span>
          <span class="text-sm font-semibold text-gray-900 dark:text-white tabular-nums w-14 text-right">{{ country.visitors }}</span>
          <span class="text-xs font-semibold tabular-nums w-12 text-right"
            :class="country.conversions > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-300 dark:text-gray-600'">
            {{ country.conversions > 0 ? `${Math.round(country.conversion_rate * 100)}%` : '—' }}
          </span>
        </button>

        <div v-if="expanded.has(country.code)" class="bg-gray-50/60 dark:bg-white/[0.02] divide-y divide-gray-100 dark:divide-gray-800/40">
          <div v-for="city in country.cities" :key="city.name"
            class="flex items-center justify-between px-4 py-1.5 pl-11">
            <span class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ city.name }}</span>
            <span class="text-xs font-medium text-gray-500 dark:text-gray-400 tabular-nums flex-shrink-0">{{ city.visitors }}</span>
          </div>
        </div>
      </div>
    </div>
  </AnalyticsCard>
</template>

<script setup>
import { ref } from 'vue'
import AnalyticsCard from '../shared/AnalyticsCard.vue'

defineProps({
  data:    { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})

const expanded = ref(new Set())

function toggle(code) {
  const next = new Set(expanded.value)
  next.has(code) ? next.delete(code) : next.add(code)
  expanded.value = next
}

// Regional indicator symbols, so no flag sprite sheet has to be loaded from a
// third party CDN just to draw a 16px flag.
function flag(alpha2) {
  if (!alpha2 || alpha2.length !== 2) return '🏳️'
  return [...alpha2.toUpperCase()].map(c => String.fromCodePoint(c.codePointAt(0) + 127397)).join('')
}
</script>
